<template>
  <main class="page-wrapper">
    <div v-if="loading">
      <spinner />
    </div>
    <section v-else-if="invoiceError" class="invoice-status" role="alert">
      <h1>{{ $t('invoiceLoadError') }}</h1>
      <button type="button" @click="retryInvoice">{{ $t('invoiceRetry') }}</button>
    </section>
    <section v-else-if="invoicePending" class="invoice-status" role="status" aria-live="polite">
      <h1>{{ $t('invoicePendingTitle') }}</h1>
      <p>{{ $t('invoicePendingDescription') }}</p>
      <button type="button" @click="retryInvoice">{{ $t('invoiceRetry') }}</button>
    </section>
    <div v-else class="base">
      <div class="flex header-container">
        <div style="display: flex; flex: 1">
          <img src="/assets/color-logo.svg" alt class="logo">
          <div class="header">
            <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 7px">
              <div class="receipt-header bold fontsize5">
                <span v-if="isR1">R1 {{ $t('invoiceNumber') }}</span>
                <span v-else>{{ $t('invoiceNumber') }}</span>
              </div>
              <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 3px">
                <div class="receipt-date-label fontsize2">{{ $t('invoiceDate') }}</div>
                <div v-if="isR1" class="receipt-date-label fontsize2">
                  {{ $t('paymentDeadline') }}
                </div>
              </div>
            </div>
            <div class="receipt-value-container">
              <div class="fontsize5">
                {{ invoiceNumber }}
              </div>
              <div class="receipt-date-value-container">
                <div class="receipt-date-label fontsize2">
                  {{ formatDateTime(date, $i18next.language) }}
                </div>
                <div v-if="isR1" class="receipt-date-label fontsize2">{{ dueDate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex body">
        <div>
          <div class="fontsize1">{{ $t('invoiceIssued') }}</div>
          <div class="receipt-owner-name bold fontsize3">CoreEvent d.o.o.</div>
          <div class="receipt-owner-adress fontsize3">Dunjevac 15, 10000 Zagreb Hrvatska</div>
          <div class="receipt-owner-oib-header fontsize1">
            {{ $t('vat') }}: <span class="receipt-owner-oib fontsize4">{{ sellerVatNumber }}</span>
          </div>
        </div>
        <div class="buyer-info-container">
          <div class="fontsize1">{{ $t('customer') }}</div>
          <div class="buyer-info bold fontsize3">
            {{ isR1 ? legalEntity.name : $t('retailCustomer') }}
          </div>
          <template v-if="isR1">
            <div class="customer-owner-address fontsize3">
              {{ legalEntity.address }}, {{ legalEntity.zipCode }} {{ legalEntity.city }}
              {{ legalEntity.country }}
            </div>
            <div class="customer-owner-oib-header fontsize1">
              {{ $t('vat') }}:
              <span class="customer-owner-oib fontsize4">{{ legalEntityVatNumber }}</span>
            </div>
          </template>
        </div>
      </div>
      <div v-if="event" class="event-info">
        <p>
          {{ $t('event') }}: <b>{{ event.title }}</b>
        </p>
        <p>
          {{ $t('eventDate') }}:
          <strong>{{ moment(event.from).format('DD.MM.YYYY') }}{{ $t('at')
          }}{{ moment(event.from).format('HH:mm [h]') }}</strong>
        </p>
      </div>
      <div class="flex table-container" style="margin-top: 1rem">
        <div class="table-header bold fontsize1">
          <div>{{ $t('inoviceNum') }}</div>
          <div>{{ $t('productDescription') }}</div>
          <div>{{ $t('unit') }}</div>
          <div>{{ $t('amount') }}</div>
          <div>{{ $t('price') }}</div>
          <div>{{ $t('tax') }}</div>
          <div>{{ $t('itemAmount') }}</div>
        </div>
        <div
          v-for="(item, name, index) in invoiceItems"
          :key="`${item[0].id}/${index}`"
          class="table-body"
        >
          <div class="table-body-item fontsize2">
            <div>* {{ index + 1 }}</div>
            <div>{{ item[0].orderItem.name }}</div>
            <div>{{ $t('pieces') }}</div>
            <div>{{ item.length }}</div>
            <div>
              {{
                isTheaterSubscription(item[0])
                  ? subscriptionUnitPrice(item).toFixed(2)
                  : Number(item[0].orderItem.priceNoVAT).toFixed(2)
              }}
            </div>
            <div>
              {{
                orderItemsWith25Vat.includes(parseInt(item[0].orderItem.id))
                  ? '25'
                  : Number(VATPercentage).toFixed(0)
              }}%
            </div>
            <div>
              {{
                isTheaterSubscription(item[0])
                  ? subscriptionLineAmount(item).toFixed(2)
                  : (item[0].orderItem.priceNoVAT * item.length).toFixed(2)
              }}
            </div>
          </div>
        </div>
        <div v-if="fixedFeeItems.length">
          <div
            v-for="(fee, index) in fixedFeeItems"
            :key="index + 1"
            class="table-body-item fontsize2"
          >
            <div>{{ Object.keys(invoiceItems).length + index + 1 }}</div>
            <div>{{ fee.name }}</div>
            <div>{{ $t('pieces') }}</div>
            <div>{{ fee.count }}</div>
            <div>
              {{ fee.singleItemPriceNoVAT.toFixed(2) }}
            </div>
            <div>25%</div>
            <div>
              {{ (fee.singleItemPriceNoVAT * fee.count).toFixed(2) }}
            </div>
          </div>
        </div>
        <div v-if="hasTicketInsurance" class="table-body-item fontsize2">
          <div>{{ Object.keys(invoiceItems).length + fixedFeeItems.length + 1 }}</div>
          <div>{{ $t('ticketInsurance') }}</div>
          <div>{{ $t('pieces') }}</div>
          <div>1</div>
          <div>{{ ticketInsuranceAmount.toFixed(2) }}</div>
          <div>0%</div>
          <div>{{ ticketInsuranceAmount.toFixed(2) }}</div>
        </div>
      </div>
      <div class="total-container fontsize2">
        <div class="total">
          <div class="total-label-style">{{ $t('totalNoTax') }}:</div>
          <div class="total-value-container">
            <div class="fontsize2">{{ totalNoVAT.toFixed(2) }} €</div>
          </div>
        </div>
        <div class="total">
          <div class="total-label-style">
            {{ $t('tax') }} ({{ Number(VATPercentage).toFixed(0) }}%), {{ $t('base') }}
            {{ totalTicketsNoVAT.toFixed(2) }} €
          </div>
          <div class="total-value-container">
            <div class="fontsize2">{{ (totalTickets - totalTicketsNoVAT).toFixed(2) }} €</div>
          </div>
        </div>
        <div v-if="fixedFeeItems.length" class="total">
          <div class="total-label-style">
            {{ $t('tax') }} ({{ coreEventFeeVAT * 100 }}%), {{ $t('base') }}
            {{ totalCoreEventFeeNoVat.toFixed(2) }} €
          </div>
          <div class="total-value-container">
            <div class="fontsize2">
              {{ round(totalCoreEventFee - totalCoreEventFeeNoVat, 2).toFixed(2) }} €
            </div>
          </div>
        </div>
        <div v-if="promoCodeDiscount" class="total">
          <div class="total-label-style">
            {{ $t('promoCodeDiscount') }}
          </div>
          <div class="total-value-container">
            <div class="fontsize2">-{{ promoCodeDiscount }} €</div>
          </div>
        </div>
        <div v-if="appliedGiftCardAmount" class="total">
          <div class="total-label-style">
            {{ $t('giftCardDiscount') }}
          </div>
          <div class="total-value-container">
            <div class="fontsize2">-{{ appliedGiftCardAmount }} €</div>
          </div>
        </div>
        <div class="total">
          <div class="total-label-style">{{ $t('inTotal') }}:</div>
          <div class="total-value-container">
            <div class="fontsize4 bold">{{ totalWithVAT.toFixed(2) }} €</div>
          </div>
        </div>
      </div>
      <div class="info-container fontsize1">
        <div class="info-item">
          <div class="info-item-label">{{ $t('paymentMethod') }}</div>
          <div class="info-item-value fontsize2">{{ paymentMethodLabel }}</div>
        </div>
        <div class="info-item">
          <div class="info-item-label">{{ $t('operator') }}</div>
          <div class="info-item-value fontsize2">Filip Gobin</div>
        </div>
        <div class="info-item">
          <div class="info-item-label">{{ $t('notes') }}</div>
          <div class="info-item-value fontsize2">
            * {{ $t('bulledInName') }} : {{ organizerLegalName }} , {{ organizerCity }}
            {{ organizerPostalCode }} ,{{ organizerAddress }}, {{ $t('vat') }}: {{ organizerOIB }}
          </div>
        </div>
      </div>
      <div
        v-if="fiscalisationInfo && fiscalisationInfo.zki && fiscalisationInfo.jir"
        style="display: flex; flex-direction: column; gap: 0.25rem"
      >
        <p class="info-item-label fontsize2">ZKI: {{ fiscalisationInfo.zki }}</p>
        <p class="info-item-label fontsize2">JIR: {{ fiscalisationInfo.jir }}</p>
        <img id="qrcode" alt="QR code">
      </div>
      <div class="footer">
        <div class="info-data-container fontsize1">
          <div class="info-item-container">
            <div>{{ $t('ourAddresses') }}</div>
            <div>Dunjevac 15, 10000 Zagreb, Hrvatska</div>
            <div>Internet: https://core-event.co/</div>
          </div>
          <div class="info-item-container">
            <div>Pravni podaci</div>
            <div>{{ $t('vat') }}: 36611335369</div>
            <div>IBAN: HR5223400091111141277</div>
            <div>SWIFT: PBZGHR2X</div>
          </div>
          <div class="info-item-container">
            <div>{{ $t('contacts') }}</div>
            <div>E-mail: support@core-event.co</div>
          </div>
        </div>
        <div class="bottom-info-container fontsize1">
          {{ $t('companyRegistered') }}
        </div>
      </div>
    </div>
    <language-switcher id="language-switcher" :event-slug="eventSlug" :is-zkm="false" />
  </main>
</template>

<script>
import moment from 'moment-timezone'
import { round } from 'lodash'
import _ from 'lodash'
import { GET_ORDER_FOR_INVOICE_BY_UUID } from '@/graphql/order.graphql'
import i18next from 'i18next'
import QRCode from 'qrcode'
import LanguageSwitcher from '@/components/shared/language-switcher.vue'
import { CoreEventFeeType, OrderItemType, OrganizationType, PromoCodeDiscountType } from '@/types'
import { convertUtcToCroatianLocalTime, getEventVAT } from '@/utils'
import { invoicePaymentMethodLabel } from '@/utils/fiscal-payment-method'
import { formatViesVatNumber, getViesVatPrefixForCountry } from '@/utils/vies'

export default {
  components: {
    LanguageSwitcher,
  },
  props: {
    orderUUID: String,
  },
  data() {
    return {
      isR1: false,
      loading: true,
      invoicePending: false,
      invoiceError: false,
      invoiceNumber: '',
      date: '',
      dueDate: '',
      organization: {},
      invoiceItems: [{}],
      subscriptionInvoiceAmounts: {},
      totalNoVAT: 0,
      fixedFeeItems: [],
      numberOfPurchasedTickets: 0,
      numberOfNonGratisPurchasedTickets: 0,
      VATPercentage: 0,
      VATAmount: 0,
      totalCoreEventFee: 0,
      totalTicketsNoVAT: 0,
      totalTickets: 0,
      fiscalisationInfo: null,
      totalCoreEventFeeNoVat: 0,
      orderItemsWith25Vat: [1088, 1089],
      coreEventFeeVAT: 0.25,
      organizerCity: '',
      totalWithVAT: 0,
      organizerOIB: null,
      referenceNumber: '',
      organizerPostalCode: '',
      organizerLegalName: '',
      eventSlug: null,
      organizerAddress: '',
      legalEntity: null,
      appliedGiftCardAmount: null,
      ticketInsuranceAmount: 0,
      hasTicketInsurance: false,
      promoCodeDiscount: 0,
      event: null,
      paymentType: null,
      kiData: null,
      giftCardAmount: null,
    }
  },
  computed: {
    sellerVatNumber() {
      const vatPrefix = this.legalEntity && getViesVatPrefixForCountry(this.legalEntity.country)

      return this.isR1 && vatPrefix && vatPrefix !== 'HR'
        ? 'HR36611335369'
        : '36611335369'
    },
    legalEntityVatNumber() {
      if (!this.legalEntity) return ''

      return formatViesVatNumber(
        this.legalEntity.OIB,
        getViesVatPrefixForCountry(this.legalEntity.country),
      )
    },
    paymentMethodLabel() {
      return invoicePaymentMethodLabel(
        { paymentType: this.paymentType, kiData: this.kiData, giftCardAmount: this.giftCardAmount },
        this.paymentType == null ? null : this.fiscalisationInfo,
        this.$i18next.language,
      )
    },
  },
  beforeDestroy() {
    this.$apollo.queries.getOrderForInvoiceByUUID.stopPolling()
  },
  methods: {
    subscriptionLineAmount(items) {
      const amount = this.subscriptionInvoiceAmounts[items[0].orderItemId]

      return amount === undefined ? items[0].orderItem.priceNoVAT * items.length : amount.total
    },
    subscriptionUnitPrice(items) {
      const amount = this.subscriptionInvoiceAmounts[items[0].orderItemId]

      return amount === undefined ? Number(items[0].orderItem.priceNoVAT) : amount.unitPrice
    },
    invoiceReadFailed() {
      this.loading = false
      this.invoiceError = true
      this.$apollo.queries.getOrderForInvoiceByUUID.stopPolling()
    },
    async retryInvoice() {
      this.invoiceError = false
      this.loading = true
      try {
        await this.$apollo.queries.getOrderForInvoiceByUUID.refetch()
      } catch (_error) {
        this.invoiceReadFailed()
      }
    },
    moment,
    async generateQrCode(data, totalPrice) {
      if (!data.jir || !data.zki) return

      setTimeout(async () => {
        const img = document.getElementById('qrcode')
        if (!img) return

        img.src = await QRCode.toDataURL(
          `https://porezna.gov.hr/rn?jir=${data.jir}&datv=${this.date}&izn=${totalPrice * 100}`,
          { margin: 0 },
        )
      }, 0)
    },
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY')
    },
    formatDateTime(date, language) {
      return moment(date, 'YYYYMMDD_HHmm').format(
        `DD.MM.YYYY. ${language === 'hr' ? '[u]' : '[at]'} HH:mm`,
      )
    },
    round(amount = 0) {
      return round(amount, 2)
    },
    calculatePromoCodeDiscount(purchases) {
      return purchases.reduce((acc, curr) => {
        if (!curr.promoCode) {
          return acc
        }

        if (curr.promoCode.discountType === PromoCodeDiscountType.AMOUNT) {
          return acc + Number(curr.promoCode.discountAmount)
        }

        return (
          acc + (Number(curr.promoCode.discountPercentage) / 100) * Number(curr.orderItem.price)
        )
      }, 0)
    },
    calculateNumberOfNonGratisTickets(purchases) {
      return purchases.filter(p => {
        let price = Number(p.orderItem.price)

        if (p.promoCode) {
          price -=
            p.promoCode.discountType === PromoCodeDiscountType.AMOUNT
              ? p.promoCode.discountAmount
              : round((Number(p.orderItem.price) * p.promoCode.discountPercentage) / 100, 2)
        }

        return price > 0
      }).length
    },
    isAnnualSportTicket(organization, purchases) {
      return (
        organization &&
        organization.type === OrganizationType.SPORT_ORGANIZATION &&
        purchases.some(purchase => purchase.prepaidCouponUserId)
      )
    },
    isTheaterSubscription(item) {
      return item.orderItem.type === 'theaterSubscription'
    },
    buildInvoiceNumber(fiscalisationInfo, invoiceNumber, createdAt) {
      if (
        fiscalisationInfo &&
        fiscalisationInfo.full_receipt &&
        fiscalisationInfo.full_receipt.designation
      ) {
        const designation = fiscalisationInfo.full_receipt.designation
        if (designation.number && designation.location_id && designation.register_number) {
          return `${designation.number}-${designation.register_number}-${designation.location_id}`
        }
      }

      const suffix = moment(createdAt).isAfter('2024/2/26 23:59') ? 12 : 10

      return `${invoiceNumber}-1-${suffix}`
    },
  },
  apollo: {
    getOrderForInvoiceByUUID: {
      query: GET_ORDER_FOR_INVOICE_BY_UUID,
      variables() {
        return {
          uuid: this.orderUUID,
        }
      },
      manual: true,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      error() {
        this.invoiceReadFailed()
      },
      result({ data, loading }) {
        if (loading) return
        if (!data || !data.getOrderForInvoiceByUUID) {
          this.invoiceReadFailed()

          return
        }
        this.invoiceError = false
        this.invoicePending = data.getOrderForInvoiceByUUID.invoicePending === true
        if (this.invoicePending) {
          this.loading = false
          this.$apollo.queries.getOrderForInvoiceByUUID.startPolling(5000)

          return
        }
        this.$apollo.queries.getOrderForInvoiceByUUID.stopPolling()
        const {
          getOrderForInvoiceByUUID: {
            createdAt,
            invoiceNumber,
            event,
            fiscalisationInfo,
            legalEntity,
            giftCardId,
            giftCardAmount,
            ticketInsuranceAmount,
            price,
            paymentType,
            kiData,
          },
        } = data
        let {
          getOrderForInvoiceByUUID: { purchases },
        } = data
        this.eventSlug = event ? event.slug : null
        this.event = event
        this.paymentType = paymentType
        this.kiData = kiData
        this.giftCardAmount = giftCardAmount
        const organization =
          (event && event.organization) ||
          (purchases[0] && purchases[0].orderItem && purchases[0].orderItem.organization)
        this.organization = organization
        this.legalEntity = legalEntity
        this.isR1 = !!legalEntity
        purchases = purchases.filter(p => !p.bundleOfferId)
        if (this.isAnnualSportTicket(organization, purchases)) {
          purchases = purchases.filter(
            purchase => purchase.orderItem.type === OrderItemType.PREPAID_COUPON,
          )
        }
        let totalFeeAmount = 0
        let totalFeeAmountNoVat = 0
        // 🔒 DEFENSIVE FEE CALCULATION - Handle both CoreEvent and kiosk fees
        this.fixedFeeItems = []

        if (fiscalisationInfo && !fiscalisationInfo.fiscalisationError) {
          // Check both items and full_receipt.items for fees, but avoid duplicates
          const itemsToCheck = []
          const processedNames = new Set()

          // Add items from full_receipt.items first (preferred source for normal orders)
          if (fiscalisationInfo['full_receipt'] && fiscalisationInfo['full_receipt'].items) {
            fiscalisationInfo['full_receipt'].items.forEach(i => {
              if (!processedNames.has(i.name)) {
                itemsToCheck.push(i)
                processedNames.add(i.name)
              }
            })
          }

          // Add items from items array (for kiosk orders)
          if (fiscalisationInfo.items) {
            fiscalisationInfo.items.forEach(i => {
              if (!processedNames.has(i.name)) {
                itemsToCheck.push(i)
                processedNames.add(i.name)
              }
            })
          }

          itemsToCheck.forEach(i => {
            // Process CoreEvent fees
            if (i.name && i.name.includes(CoreEventFeeType.TICKET)) {
              // Handle different data structures
              let itemCount, totalPrice, singleItemPrice

              if (i.quantity !== undefined) {
                // Kiosk order structure: {quantity, price}
                itemCount = i.quantity
                totalPrice = i.price * i.quantity
                singleItemPrice = i.price
              } else {
                // Normal order structure: {amt, price, single_item_price}
                itemCount = i.amt / 1000
                totalPrice = i.price / 100
                singleItemPrice = i['single_item_price']
                  ? i['single_item_price'] / 100
                  : totalPrice / itemCount
              }
              totalFeeAmount += totalPrice
              totalFeeAmountNoVat += totalPrice / 1.25

              this.fixedFeeItems.push({
                name: i.name,
                count: itemCount,
                totalPrice: totalPrice,
                singleItemPrice: singleItemPrice,
                singleItemPriceNoVAT: Number(singleItemPrice) / 1.25,
              })
            }

            // Process kiosk fees
            if (i.name && i.name.includes('Kiosk naknada')) {
              let itemCount, totalPrice, singleItemPrice
              if (i.quantity !== undefined) {
                // Kiosk order structure: {quantity, price}
                itemCount = i.quantity
                totalPrice = i.price * i.quantity
                singleItemPrice = i.price
              } else {
                // Normal order structure: {amt, price, single_item_price}
                itemCount = i.amt / 1000
                totalPrice = i.price / 100
                singleItemPrice = i['single_item_price']
                  ? i['single_item_price'] / 100
                  : totalPrice / itemCount
              }

              totalFeeAmount += totalPrice
              totalFeeAmountNoVat += totalPrice / 1.25

              this.fixedFeeItems.push({
                name: i.name,
                count: itemCount,
                totalPrice: totalPrice,
                singleItemPrice: singleItemPrice,
                singleItemPriceNoVAT: Number(singleItemPrice) / 1.25,
              })
            }
          })
        }
        this.invoiceNumber = this.buildInvoiceNumber(fiscalisationInfo, invoiceNumber, createdAt)
        this.VATPercentage = this.event
          ? getEventVAT(this.event)
          : (organization && organization.VATPercentage) || 0
        if (this.event && this.event.id === 3981) {
          this.VATPercentage = '25.00'
        }
        if (
          this.organization &&
          this.organization.id === 17 &&
          purchases.some(p => p.membershipUserId)
        ) {
          this.VATPercentage = '0.00'
        }

        if (fiscalisationInfo && fiscalisationInfo.publishTime) {
          this.date = convertUtcToCroatianLocalTime(fiscalisationInfo.publishTime, 'YYYYMMDD_HHmm')
        } else if (
          fiscalisationInfo &&
          fiscalisationInfo['full_receipt'] &&
          fiscalisationInfo['full_receipt'].issue_date_time
        ) {
          this.date = moment
            .unix(fiscalisationInfo['full_receipt'].issue_date_time)
            .tz('Europe/Zagreb')
            .format('YYYYMMDD_HHmm')
        } else {
          this.date = moment(createdAt).format('YYYYMMDD_HHmm')
        }
        this.numberOfPurchasedTickets = purchases.length
        this.numberOfNonGratisPurchasedTickets = this.calculateNumberOfNonGratisTickets(purchases)

        // Deterministic per-item pricing independent of fiscalisation payload
        // 1) Detect order-level promocode (all purchases share the same promoCodeId)
        const firstPromoId =
          (purchases[0] && purchases[0].promoCode && purchases[0].promoCode.id) ||
          (purchases[0] && purchases[0].promoCodeId)
        const hasOrderLevelPromo =
          !!firstPromoId &&
          purchases.every(function (p) {
            var pid = (p.promoCode && p.promoCode.id) || p.promoCodeId

            return pid === firstPromoId
          })

        // 2) Compute base total for ratio
        const baseTotal = purchases.reduce(function (sum, p) {
          return sum + (parseFloat(p.orderItem.price) || 0)
        }, 0)

        // 3) Determine order-level discount ratio when applicable
        let discountRatio = 1
        if (hasOrderLevelPromo) {
          const promoHolder = purchases.find(function (p) {
            return !!(p.promoCode || p.promoCodeId)
          })
          const promo = promoHolder && promoHolder.promoCode
          if (promo) {
            if (promo.discountType === PromoCodeDiscountType.AMOUNT) {
              const orderLevelDiscount = Math.min(Number(promo.discountAmount) || 0, baseTotal)
              discountRatio = baseTotal > 0 ? (baseTotal - orderLevelDiscount) / baseTotal : 1
            } else if (promo.discountType === PromoCodeDiscountType.PERCENTAGE) {
              discountRatio = (100 - Number(promo.discountPercentage || 0)) / 100
            }
          }
        }

        // 4) Apply per-item price calculation (prefer per-item promo when present, else order-level ratio)
        let totalPromoDiscount = 0
        purchases.forEach(purchase => {
          // Preserve original gross price before we modify it
          const originalPrice = parseFloat(purchase.orderItem.price) || 0
          purchase.__originalPrice = originalPrice
          let discountedPrice = originalPrice

          if (purchase.promoCode) {
            if (purchase.promoCode.discountType === PromoCodeDiscountType.AMOUNT) {
              discountedPrice = Math.max(
                0,
                originalPrice - Number(purchase.promoCode.discountAmount || 0),
              )
            } else {
              discountedPrice =
                originalPrice * ((100 - Number(purchase.promoCode.discountPercentage || 0)) / 100)
            }
          } else if (hasOrderLevelPromo) {
            discountedPrice = originalPrice * discountRatio
          }

          // Compute priceNoVAT using per-item VAT when applicable (fallback to org VAT)
          const purchaseVAT = this.orderItemsWith25Vat.includes(parseInt(purchase.orderItem.id))
            ? 25
            : Number(this.VATPercentage || 0)

          purchase.orderItem.price = Number(discountedPrice.toFixed(2))
          purchase.orderItem.priceNoVAT = discountedPrice / (1 + purchaseVAT / 100)

          // Track total promo/gift discounts (gross) for a single summary line
          const origDbPrice = parseFloat(purchase.__originalPrice || originalPrice)
          const discountDelta = Math.max(0, (origDbPrice || 0) - discountedPrice)
          totalPromoDiscount += discountDelta
        })

        this.totalCoreEventFee = round(totalFeeAmount, 2)
        i18next.changeLanguage(
          (organization && organization.preferredLanguage) == 'en' ? 'en' : 'hr',
        )
        this.promoCodeDiscount = round(totalPromoDiscount, 2)
        this.hasTicketInsurance = !!ticketInsuranceAmount
        this.ticketInsuranceAmount = this.hasTicketInsurance ? round(ticketInsuranceAmount, 2) : 0

        // Use fiscalized total if available, otherwise use deterministic expected total
        if (
          fiscalisationInfo &&
          fiscalisationInfo.full_receipt &&
          fiscalisationInfo.full_receipt.items
        ) {
          // Calculate total from fiscalized items; support legacy cents format
          const fiscalizedTotal =
            (typeof fiscalisationInfo.full_receipt.total !== 'undefined'
              ? Number(fiscalisationInfo.full_receipt.total || 0) / 100
              : fiscalisationInfo.full_receipt.items.reduce((sum, item) => {
                if (typeof item.quantity !== 'undefined') {
                  return sum + Number(item.price || 0) * Number(item.quantity || 0)
                }

                return sum + Number(item.price || 0) / 100
              }, 0)) || 0

          // Calculate expected total from purchases (including promocode discounts)
          const expectedTotal =
            purchases.reduce(
              (sum, purchase) => sum + (parseFloat(purchase.orderItem.price) || 0),
              0,
            ) +
            this.totalCoreEventFee +
            this.ticketInsuranceAmount

          // If fiscalized total doesn't match expected total (due to promocode grouping issues),
          // use the expected total instead
          if (Math.abs(fiscalizedTotal - expectedTotal) > 0.01) {
            this.totalWithVAT = expectedTotal
          } else {
            this.totalWithVAT = fiscalizedTotal
          }
        } else {
          // Deterministic total from purchases + fees
          const expectedTotal =
            purchases.reduce(
              (sum, purchase) => sum + (parseFloat(purchase.orderItem.price) || 0),
              0,
            ) +
            this.totalCoreEventFee +
            this.ticketInsuranceAmount
          this.totalWithVAT = Number(expectedTotal.toFixed(2))
        }

        this.totalTicketsNoVAT = purchases.reduce((acc, item) => {
          const nv = parseFloat(item.orderItem.priceNoVAT) || 0

          return acc + nv
        }, 0)

        // Deterministic tickets total: sum of discounted per-item prices
        this.totalTickets = purchases.reduce(
          (sum, purchase) => sum + (parseFloat(purchase.orderItem.price) || 0),
          0,
        )

        this.totalNoVAT = this.totalTicketsNoVAT
        if (this.totalCoreEventFee) {
          this.totalCoreEventFeeNoVat = totalFeeAmountNoVat
          this.totalNoVAT = round(this.totalCoreEventFeeNoVat + this.totalNoVAT, 2)
        }
        this.totalNoVAT = round(this.totalNoVAT + this.ticketInsuranceAmount, 2)
        this.VATAmount = round(this.totalWithVAT - this.totalNoVAT, 2)

        // if gift card is applied, price is reduced by gift card amount
        if (giftCardId) {
          this.appliedGiftCardAmount =
            (giftCardId && giftCardAmount && giftCardAmount.split(' ')[1]) || '0.00'
          this.totalWithVAT =
            (parseFloat(this.totalWithVAT) || 0) - (parseFloat(this.appliedGiftCardAmount) || 0)
        }

        // Display line items at original price; show promo discount near totals
        const displayPurchases = purchases.map(p => {
          const original = Number(p.__originalPrice || p.orderItem.price)
          const vatPct = this.orderItemsWith25Vat.includes(parseInt(p.orderItem.id))
            ? 25
            : Number(this.VATPercentage || 0)

          return {
            ...p,
            orderItem: {
              ...p.orderItem,
              priceNoVAT: original / (1 + vatPct / 100),
            },
          }
        })
        this.invoiceItems = _.groupBy(displayPurchases, p => p.orderItemId)
        this.subscriptionInvoiceAmounts = {}

        this.organizerLegalName = (organization && organization.legalName) || ''
        this.organizerAddress = (organization && organization.address) || ''
        this.organizerCity = (organization && organization.city) || ''
        this.organizerPostalCode = (organization && organization.postalCode) || ''
        this.organizerOIB = (organization && organization.OIB) || ''

        if (fiscalisationInfo) {
          this.fiscalisationInfo = fiscalisationInfo
          this.generateQrCode(this.fiscalisationInfo, this.totalWithVAT)
        }
        const isSubscription =
          purchases.length > 0 && purchases.every(p => p.orderItem.type === 'theaterSubscription')

        if (isSubscription) {
          const orderPrice = parseFloat((price || 'EUR 0').split(' ')[1]) || 0
          this.totalWithVAT = orderPrice + this.ticketInsuranceAmount
          const vatRate = (this.VATPercentage || 0) / 100
          this.totalTickets = orderPrice
          this.totalTicketsNoVAT = this.totalTickets / (1 + vatRate)
          this.totalNoVAT = this.totalTicketsNoVAT + this.ticketInsuranceAmount
          this.VATAmount = round(this.totalWithVAT - this.totalNoVAT, 2)

          // Allocate the paid amount across purchased passes, not event entitlements.
          // Keep the last line as the remainder so rounded line totals match the invoice.
          const groups = Object.entries(this.invoiceItems)
          const totalWeight = purchases.reduce((sum, p) => sum + Number(p.orderItem.price), 0)
          let allocated = 0
          groups.forEach(([id, items], index) => {
            const weight = items.reduce((sum, p) => sum + Number(p.orderItem.price), 0)
            const share = totalWeight > 0 ? weight / totalWeight : items.length / purchases.length
            const amount =
              index === groups.length - 1
                ? round(this.totalTicketsNoVAT - allocated, 2)
                : round(this.totalTicketsNoVAT * share, 2)
            this.subscriptionInvoiceAmounts[id] = {
              total: amount,
              unitPrice: (this.totalTicketsNoVAT * share) / items.length,
            }
            allocated += amount
          })
        }
        this.loading = false
      },
    },
  },
}
</script>

<style scoped>
.invoice-status {
  margin: auto;
  padding: 2rem;
  text-align: center;
  font-family: OpenSans, sans-serif;
}

.invoice-status h1 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
}

.invoice-status button {
  margin-top: 1.5rem;
  padding: 0.6rem 1rem;
  border: 1px solid currentColor;
  border-radius: 4px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  @page {
    size: A4;
    margin: 0;
  }

  body {
    margin: 1.6cm;
  }
}

html,
body {
  height: 100%;
}

main {
  height: 297mm;
  padding: 0.33333in 0.22222in 0.33333in 0.44444in;
  width: 210mm;
}

header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.33333in;
}

hr {
  margin-bottom: 0.625rem;
}

.logo {
  height: 0.66666in;
}

.page-wrapper {
  display: flex;
  flex-direction: column-reverse;
}

#language-switcher {
  align-self: end;
}

.event-info {
  margin-top: 1rem;
  font-family: OpenSans;
  font-size: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-indent: 8px;
}

@media print {
  #language-switcher {
    display: none;
  }
}

.flex {
  display: flex;
  /*  background-color:rgba(99, 99, 99, 0.2);  */
}

.base {
  font-family: OpenSans;
  font-weight: normal;
  line-height: 1;
  letter-spacing: 0;
  unicode-bidi: bidi-override;
  -moz-font-feature-settings: 'liga' 0;
  height: 100%;
  position: relative;
}

.bold {
  font-weight: bold;
  line-height: 1;
  word-spacing: 0;
}

.fontsize1 {
  font-size: 0.5rem;
}

.fontsize2 {
  font-size: 0.6rem;
}

.fontsize3 {
  font-size: 0.65rem;
}

.fontsize4 {
  font-size: 0.7rem;
}

.fontsize5 {
  font-size: 1rem;
}

.gray {
  color: rgb(102 102 102);
}

@font-face {
  font-family: OpenSans;
  src: url('./fonts/OpenSans-vf.ttf') format('truetype');
  font-weight: normal;
}

@font-face {
  font-family: OpenSans;
  src: url('./fonts/OpenSans-Bold.ttf') format('truetype');
  font-weight: bold;
}

.header {
  display: flex;
  flex-direction: row;
  gap: 0.8em;
  min-width: 14.6875rem;
  margin-top: 8px;
  margin-left: auto;
}

.header-container {
  justify-content: flex-start;
}

.receipt-header {
  letter-spacing: -0.5px;
}

.receipt-date-label {
  letter-spacing: -0.25px;
}

.receipt-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.receipt-date-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

.body {
  margin-top: 3.0313rem;
  margin-left: 8px;
}

.receipt-owner-name {
  margin-top: 3.5px;
  letter-spacing: 0.25px;
}

.receipt-owner-adress,
.customer-owner-address {
  margin-top: 3.5px;
  letter-spacing: 0.25px;
}

.receipt-owner-oib-header,
.customer-owner-oib-header {
  margin-top: 5.5px;
}

.receipt-owner-oib,
.customer-owner-oib {
  margin-top: 3px;
}

.buyer-info-container {
  margin-left: auto;
  margin-top: 0.5px;
  width: 235px;
}

.buyer-info {
  letter-spacing: 0.25px;
  margin-top: 3px;
}

.table-container {
  display: flex;
  flex-direction: column;
  margin-top: 2.5313rem;
}

.table-header {
  flex-grow: 1;
  display: grid;
  /*  grid-template-columns: 25fr 270fr 40fr 60fr 20fr 35fr 60fr;  */
  grid-template-columns: 4.5% 51% 8% 12% 7% 7% auto;
  padding-top: 0;
  padding-left: 0;
  padding-bottom: 0;
  margin-top: 9.5px;
  margin-left: 8.5px;
  margin-bottom: 7px;
  margin-right: 5px;
}

.table-header div:last-child {
  justify-self: end;
}

.table-body {
  display: flex;
  flex-direction: column;
}

.table-body-item {
  flex-grow: 1;
  display: grid;
  grid-template-columns: 5% 51% 8% 12% 7% 7% auto;
  padding-top: 8.5px;
  padding-bottom: 18px;
  padding-left: 5px;
  padding-right: 5px;
}

.table-body-item div:last-child {
  justify-self: end;
}

.total-container {
  margin-top: 18px;
}

.total {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.total-label-style {
  flex-grow: 35;
  flex-basis: 0;
  text-align: end;
}

.total-value-container {
  flex-grow: 10;
  flex-basis: 0;
  text-align: end;
  margin-right: 5px;
}

.info-container {
  margin-left: 5px;
  padding-top: 2px;
}

.info-item {
  margin-bottom: 9px;
}

.info-item-label {
  color: rgb(85, 85, 85);
}

.info-item-value {
  margin-top: 3px;
}

.info-data-container {
  display: flex;
  flex-wrap: nowrap;
  margin-top: auto;
}

.info-item-container {
  flex-grow: 1;
  color: rgb(85, 85, 85);
}

.info-item-container:last-child {
  flex-shrink: 1;
}

.bottom-info-container {
  margin-top: 20px;
}

.footer {
  position: absolute;
  bottom: 0;
}

#qrcode {
  height: 75px !important;
  width: 75px !important;
}
</style>
